import * as d3 from 'd3';
import * as _ from 'lodash';

import { ISeries } from './series/ISeries';
import { ILegend } from './ILegend';
import { IMargins } from '../../models/IMargins';
import { Margins } from '../../models/Margins';
import { RoundingHelper, ILogger, LoggerFactory } from '../../helpers/helpers';
import { getRandomInt32 } from '../../../common/helpers/getRandom';

interface LegendDispatch extends d3.Dispatch {
    hover: (d, i) => void;
    click: (d, i) => void;
}

interface Size {
    itemHeight: number;
    margin: number;
    offset: number;
    rectSize: number;
    textHeight: number;
    width: number;
}

export class Legend implements ILegend {
    private _dispatch: LegendDispatch;
    private _fraction = 0.2;
    private _height: number;
    private _isResizing;
    private _isVisible;
    private _legendGroup: d3.Selection<any>;
    private _legendHeight: number;
    private _legendWidth: number;
    private _logger: ILogger;
    private _margins: IMargins;
    private _translateX: number;
    private _translateY: number;
    private _width: number = 50;

    constructor(container: d3.Selection<any> | d3.selection.Enter<any>) {
        this._legendWidth = 150;
        this._isVisible = true;
        this._margins = new Margins(0, 0, 0, 0);
        this.init(container);
        this._logger = LoggerFactory.getLogger('WideTech.Framework.Legend');
    }

    private init(container: d3.Selection<any> | d3.selection.Enter<any>) {
        this._legendGroup = container
            .append('g')
            .classed('legend-group', true)
            .classed('hidden-group', true);
        const id = `legendClipPath${getRandomInt32()}`;
        this._legendGroup.append('defs')
            .append('clipPath')
            .attr('id', id)
            .append('rect')
            .classed('legend-clippath-rectangle', true)
            .attr({
                'x': 0,
                'y': 0
            });
        this._legendGroup.append('rect')
            .classed('legend-rectangle', true)
            .attr({
                'x': 0,
                'y': 0
            });
        this._legendGroup.append('g')
            .classed('legend-items', true)
            .style('clip-path', `url("#${id}")`);

        this._dispatch = (d3.dispatch('hover', 'click', 'mouseout') as LegendDispatch);
        this.width(20);
        this._height = 20;
        this.computeSize(0, 0);
    }

    series(series: ISeries[]): this {
        series = series.filter(s => s.isXAxis === undefined || (!!s.isXAxis && !s.isXAxis())
            && s.identifier
            && (typeof s.identifier !== 'function'
                || (typeof s.identifier === 'function'
                    && (!s.identifier().indexOf
                        || s.identifier().indexOf('missing') !== 0))));
        const size = this.computeSize(this._width, this._height);

        const legendItemGroup = this._legendGroup
            .select('.legend-items')
            .selectAll('.legend-item-group');

        // recreating text-container is mandatory to update popover
        // doing it here because update is called on each resize
        legendItemGroup
            .selectAll('.text-container')
            .remove();

        legendItemGroup
            .append('g')
            .classed('text-container', true)
            .append('text')
            .classed('legend-text', true);

        const selection = legendItemGroup.data(series);

        this.enter(selection)
            .update(selection, size)
            .exit(selection);

        const height = series.length * size.itemHeight + 1 * size.offset;
        this.updateRects(size.width, height);
        this.translate(this._translateX, this._translateY);

        return this;
    }

    private updateRects(width: number, height: number) {
        width = isNaN(width) ? 0 : width;
        this._legendHeight = height;
        this._legendGroup
            .select('.legend-rectangle')
            .attr('height', height)
            .attr('width', width);
        this._legendGroup
            .select('.legend-clippath-rectangle')
            .attr('height', height)
            .attr('width', width);
    }

    private exit(selection: d3.selection.Update<any>): this {
        selection.exit().remove();
        return this;
    }

    private dIdentifier(d: ISeries): string {
        return (d.identifier instanceof Function)
            ? d.identifier()
            : d.identifier;
    }

    private dIsVisible(d: any): boolean {
        return (d.isVisible instanceof Function)
            ? d.isVisible()
            : d.isVisible;
    }

    private dColor(d: any): string {
        return (d.color instanceof Function)
            ? d.color()
            : d.color;
    }

    private dLegendText(d: any): string {
        return (d.legendText instanceof Function)
            ? d.legendText()
            : d.legendText;
    }

    private update(selection: d3.Selection<ISeries>, size: Size) {
        try {

            const { itemHeight, textHeight, rectSize, offset } = size;

            const self = this;
            const legendItemGroup = selection
                .attr('id', d => `item${self.dIdentifier(d)}`)
                .attr('transform', (d, i) => `translate(${offset},${i * itemHeight + offset})`)
                .style('opacity', d => self.dIsVisible(d) ? 1 : 0.5);

            legendItemGroup.select('rect')
                .attr({
                    'width': rectSize,
                    'height': rectSize,
                    'fill': d => self.dColor(d)
                })
                .on('mouseover', (d, i) => this._dispatch.hover(d, i))
                .on('mouseout', (d, i) => this._dispatch.hover(undefined, i))
                .on('click', (d, i) => this._dispatch.click(d, i));

            const text = legendItemGroup
                .select('.text-container')
                .classed('clickable', d => !!d.click)
                .on('click', (d, i) => {
                    if (d.click) {
                        d.click(d, i);
                    }
                })
                .each(function (d) {
                    self.setLegendAtributes(d3.select(this), d);
                })
                .select('text');

            text.attr({
                'x': rectSize + size.margin,
                'y': textHeight
            });
            text.text(d => self.dLegendText(d));

        } catch (e) {
            this._logger.error(e);
        }
        return this;
    }

    private enter(selection: d3.selection.Update<ISeries>) {
        try {
            const legendItemGroup = selection
                .enter()
                .append('g')
                .classed('legend-item-group', true);

            legendItemGroup.append('rect')
                .attr({ 'x': 0, 'y': 0 });

            legendItemGroup
                .append('g')
                .classed('text-container', true)
                .append('text')
                .classed('legend-text', true);
        } catch (e) {
            this._logger.error(e);
        }
        return this;
    }

    translate(translateX: number, translateY: number): void {
        this._translateX = isNaN(translateX) ? 0 : translateX;
        this._translateY = isNaN(translateY) ? 0 : translateY;
        this._legendGroup
            .transition()
            .attr({ 'transform': `translate(${this.computeTranslateX()}, ${this.computeTranslateY()})` });
    }

    private computeTranslateX() {
        const w = RoundingHelper.notNaN(this.width(), 0);
        const result = RoundingHelper.notNaN(this._translateX - w + this._margins.marginLeft * (this.ratio() || 1), 0);
        return result;
    }

    private computeTranslateY(): number {
        const h = RoundingHelper.notNaN(this._legendHeight, 0);
        return RoundingHelper.notNaN(this._translateY - h / 2, 0);
    }

    isResizing(value?: boolean): boolean {
        if (value !== null && value !== undefined) {
            this._isResizing = value;
        }
        return this._isResizing;
    }

    isVisible(value?: boolean): boolean {
        if (value !== null && value !== undefined) {
            this._isVisible = value;
            this._legendGroup.classed('hidden-group', !this._isVisible);
        }
        return this._isVisible;
    }

    width(value?: number): number {
        if (arguments.length) {
            this._legendWidth = value;
        }
        return (this.isVisible() || this.isResizing())
            ? this._legendWidth
            : 0;
    }

    height(value?: number): number {
        if (arguments.length) {
            this._legendHeight = value;
        }
        return (this.isVisible() || this.isResizing())
            ? (this._legendHeight)
            : 0;
    }

    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            return this;
        }
        return this._ratio;
    }

    group(): d3.Selection<any> {
        return this._legendGroup;
    }

    resize(width, height) {
        this._width = width;
        this._height = height;
        const size = this.computeSize(width, height);
        const items = this._legendGroup.selectAll('.legend-item-group');
        this.update(items, size);
        const h = items[0].length * size.itemHeight + size.offset;
        this.updateRects(size.width, h);
    }

    reselectAndResize() {
        if (this._isVisible && this._legendGroup.length > 0) {
            const items = d3.select(this._legendGroup[0].parentNode).selectAll('.legend-item-group');
            const size = this.computeSize(this._width, this._height);
            this.update(items, size);
        }
    }

    margins(value?: IMargins): IMargins {
        if (arguments.length) {
            this._margins = value;
        }
        return this._margins;
    }

    private computeSize(width: number, height: number): Size {
        const ratio = this.ratio() || 1;
        let w = this._fraction * width;
        w = Math.min(w, 150 * ratio);
        this.width(w);
        const margin = 5 * ratio;
        const offset = 8 * ratio;
        const rectSize = offset;
        const itemHeight = rectSize + 2 * margin;
        const textHeight = .9 * rectSize;
        return {
            width: w,
            itemHeight,
            offset,
            rectSize,
            textHeight,
            margin
        };
    }

    on(evt: string, calback: (d, i) => void) {
        this._dispatch.on(evt, calback);
    }

    private setLegendAtributes(elem: d3.Selection<any>, series: ISeries) {
        if (series.legendAttributes && series.legendAttributes()) {
            const attrs = series.legendAttributes();
            for (const key of Object.keys(attrs)) {
                elem.attr(key, attrs[key]);
            }
        }
    }
}